import { useState, type FormEvent } from 'react';
import { CheckCircle2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAreas,
  useCompleteTask,
  useCreateTask,
  useDeleteTask,
  useTasks,
  type Task,
} from '../lib/hooks';
import { ConfirmDeleteButton } from '../components/confirm-delete-button';
import { useT } from '../lib/i18n';
import { Modal } from '../components/modal';

const PRIORITY: Array<{ value: Task['priority']; label: string; dot: string }> = [
  { value: 'urgent', label: 'Urgent', dot: 'bg-rose-400' },
  { value: 'high', label: 'High', dot: 'bg-amber-400' },
  { value: 'medium', label: 'Medium', dot: 'bg-indigo-400' },
  { value: 'low', label: 'Low', dot: 'bg-slate-400' },
];

export function TasksPage() {
  const t = useT();
  const [areaFilter, setAreaFilter] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);

  const tasks = useTasks(areaFilter ? { areaId: areaFilter } : undefined);
  const areas = useAreas();
  const create = useCreateTask();
  const complete = useCompleteTask();
  const remove = useDeleteTask();

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<Task['priority']>('medium');
  const [taskAreaId, setTaskAreaId] = useState('');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    await create.mutateAsync({
      title: title.trim(),
      priority,
      areaId: taskAreaId || null,
      status: 'todo',
    });
    setTitle('');
    setTaskAreaId('');
    setPriority('medium');
    setModalOpen(false);
    toast.success('Đã tạo task');
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 lg:p-8">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t('tasks.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {tasks.data?.length ?? 0} task — filter theo Area để focus
          </p>
        </div>
        <div className="flex gap-2">
          <select
            className="input w-48"
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
          >
            <option value="" className="bg-slate-900">
              Tất cả Area
            </option>
            {areas.data?.map((a) => (
              <option key={a.id} value={a.id} className="bg-slate-900">
                {a.name}
              </option>
            ))}
          </select>
          <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('tasks.add')}
          </button>
        </div>
      </header>

      <div className="panel">
        {tasks.isLoading ? (
          <div className="panel-body text-sm text-slate-500">{t('common.loading')}</div>
        ) : tasks.data && tasks.data.length > 0 ? (
          <ul className="divide-y divide-slate-200 dark:divide-white/5">
            {tasks.data.map((task) => {
              const pAccent =
                PRIORITY.find((p) => p.value === task.priority)?.dot ?? 'bg-slate-400';
              return (
                <li
                  key={task.id}
                  className="group flex items-center gap-3 px-6 py-4 transition hover:bg-slate-50/60 dark:bg-white/[0.02]"
                >
                  <button
                    type="button"
                    onClick={() => complete.mutate(task.id)}
                    disabled={task.status === 'done'}
                    className={
                      task.status === 'done'
                        ? 'text-emerald-400'
                        : 'text-slate-400 dark:text-slate-600 hover:text-emerald-300'
                    }
                  >
                    <CheckCircle2 className="h-5 w-5" />
                  </button>
                  <span className={`h-2 w-2 flex-shrink-0 rounded-full ${pAccent}`} />
                  <div className="flex-1">
                    <div
                      className={
                        task.status === 'done'
                          ? 'text-sm text-slate-500 line-through'
                          : 'text-sm font-medium text-slate-900 dark:text-slate-100'
                      }
                    >
                      {task.title}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      <span className="badge">{task.status}</span>
                      <span className="badge">{task.priority}</span>
                      {task.areaId && (
                        <span className="badge-info">
                          {areas.data?.find((a) => a.id === task.areaId)?.name ?? '—'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="opacity-100 md:opacity-60 md:group-hover:opacity-100 transition">
                    <ConfirmDeleteButton
                      size="md"
                      label="Xoá task"
                      disabled={remove.isPending}
                      onConfirm={() => {
                        remove.mutate(task.id, {
                          onSuccess: () => toast.success(`Đã xoá "${task.title}"`),
                          onError: (e) =>
                            toast.error(e instanceof Error ? e.message : 'Không xoá được'),
                        });
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="panel-body py-12 text-center text-sm text-slate-500">
            {t('tasks.empty')}
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('tasks.add')}
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              form="task-form"
              className="btn-primary"
              disabled={create.isPending}
            >
              {create.isPending ? '…' : t('common.save')}
            </button>
          </>
        }
      >
        <form id="task-form" onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">Tiêu đề</label>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Priority</label>
              <select
                className="input"
                value={priority}
                onChange={(e) => setPriority(e.target.value as Task['priority'])}
              >
                {PRIORITY.map((p) => (
                  <option key={p.value} value={p.value} className="bg-slate-900">
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Area</label>
              <select
                className="input"
                value={taskAreaId}
                onChange={(e) => setTaskAreaId(e.target.value)}
              >
                <option value="" className="bg-slate-900">
                  — Không gắn —
                </option>
                {areas.data?.map((a) => (
                  <option key={a.id} value={a.id} className="bg-slate-900">
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
